import { useEffect, useState, type ReactNode } from 'react';
import { Monitor } from 'lucide-react';

/** 利用不可と判定するウィンドウ幅（px 未満） */
export const MIN_VIEWPORT_WIDTH = 1024;

const MOBILE_UA = /Mobi|Android|iPhone|iPad|iPod/i;

export interface DeviceInfo {
  userAgent: string;
  maxTouchPoints: number;
  width: number;
}

export type BlockReason = 'device' | 'width' | null;

/** モバイル端末か（iPadOS の「デスクトップ用 Web サイト」= Mac 偽装も判定） */
export function isMobileDevice(userAgent: string, maxTouchPoints: number): boolean {
  if (MOBILE_UA.test(userAgent)) return true;
  // iPadOS 13+ は既定で Macintosh を名乗る。Mac 実機はタッチ点数 0〜1
  if (/Macintosh/i.test(userAgent) && maxTouchPoints > 1) return true;
  return false;
}

export function getBlockReason(info: DeviceInfo): BlockReason {
  if (isMobileDevice(info.userAgent, info.maxTouchPoints)) return 'device';
  if (info.width < MIN_VIEWPORT_WIDTH) return 'width';
  return null;
}

function readDevice(): DeviceInfo {
  return {
    userAgent: navigator.userAgent,
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
    width: window.innerWidth,
  };
}

/**
 * PC 専用ガード。スマートフォン・タブレット、または幅 1024px 未満のウィンドウでは
 * 利用不可画面を表示し、アプリ本体（children）を一切描画しない。
 */
export function PcOnlyGuard({ children }: { children: ReactNode }) {
  const [reason, setReason] = useState<BlockReason>(() => getBlockReason(readDevice()));

  useEffect(() => {
    const onResize = () => setReason(getBlockReason(readDevice()));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    document.body.classList.toggle('bs-blocked', reason !== null);
  }, [reason]);

  // 一度でも利用可能になった後に幅だけが不足した場合（ウィンドウの縮小・画面分割など）は、
  // アプリ本体をアンマウントせず非表示にして入力中の内容・ダイアログを保持する（障害 BUG-003）。
  const [everAllowed, setEverAllowed] = useState(reason === null);
  useEffect(() => {
    if (reason === null) setEverAllowed(true);
  }, [reason]);

  // 起動時からモバイル・幅不足の場合はアプリ本体を一切描画しない
  if (reason === 'device' || (reason !== null && !everAllowed)) return <UnsupportedScreen reason={reason} />;
  // 要素の構造を変えない（常に同じラッパー配下に children を置く）ことで再マウントを防ぐ
  return (
    <>
      <div className="bs-pc-guard" style={{ display: reason === null ? 'contents' : 'none' }} aria-hidden={reason !== null || undefined}>
        {children}
      </div>
      {reason !== null && <UnsupportedScreen reason={reason} />}
    </>
  );
}

export function UnsupportedScreen({ reason }: { reason: Exclude<BlockReason, null> }) {
  return (
    <main className="bs-unsupported" role="alert" aria-live="assertive">
      <div className="bs-unsupported__card">
        <div className="bs-unsupported__icon" aria-hidden>
          <Monitor size={30} />
        </div>
        <p className="bs-unsupported__eyebrow">外国漁船操業等調査・監視事業システム</p>
        <h1 className="bs-unsupported__title">パソコンでご利用ください</h1>
        {reason === 'device' ? (
          <p className="bs-unsupported__text">
            本システムはパソコン専用です。
            <br />
            スマートフォン・タブレットからはご利用いただけません。
          </p>
        ) : (
          <p className="bs-unsupported__text">
            画面の幅が不足しています（{MIN_VIEWPORT_WIDTH}px 以上が必要です）。
            <br />
            ブラウザのウィンドウを広げるか、表示倍率を下げてください。
          </p>
        )}
        <p className="bs-unsupported__note">推奨環境：Windows / macOS の Microsoft Edge・Google Chrome 最新版、画面幅 1280px 以上</p>
      </div>
    </main>
  );
}
